import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, LogOut, Printer } from "lucide-react";
import { api, peso, pesoShort, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card, Btn, Field, Kpi, LiveTimer, cx, useInterval } from "../../components/kit";

const DENOMS = [1000, 500, 200, 100, 50, 20, 10, 5, 1];

export default function AdminApp() {
  const { user, logout } = useAuth();
  if (!user) return <AdminLogin />;
  if (!["admin", "superadmin"].includes(user.role)) return <NotAdmin logout={logout} />;
  return <AdminDashboard user={user} logout={logout} />;
}

function NotAdmin({ logout }) {
  return (
    <div className="max-w-md mx-auto min-h-screen px-4 pt-20 pb-28 text-center">
      <Card><p className="text-slate-300">This account is not an admin. Please log out and sign in with the owner account.</p>
        <Btn className="w-full mt-4" onClick={logout}>Log out</Btn></Card>
    </div>
  );
}

function AdminLogin() {
  const { loginAdmin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try { const u = await loginAdmin(email, password); if (!["admin", "superadmin"].includes(u.role)) toast.error("Not an admin account"); else toast.success("Welcome"); }
    catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };
  return (
    <div className="max-w-md mx-auto min-h-screen px-4 pt-16 pb-28">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-cyan-500 flex items-center justify-center"><ShieldCheck className="text-slate-950" size={22} /></div>
        <h1 className="font-head text-lg font-extrabold">Cool Car Admin</h1>
      </div>
      <Card className="animate-in">
        <h2 className="font-head text-xl font-bold mb-1">Owner Login</h2>
        <p className="text-sm text-slate-400 mb-5">Command center access.</p>
        <div className="space-y-3">
          <Field label="Email" testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Field label="Password" type="password" testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <Btn className="w-full" data-testid="admin-login-btn" onClick={submit} disabled={loading}>{loading ? "Signing in..." : "Sign In"}</Btn>
        </div>
      </Card>
    </div>
  );
}

function AdminDashboard({ user, logout }) {
  const [tab, setTab] = useState("dash");
  const tabs = [["dash", "Home"], ["pay", "Payments"], ["prices", "Prices"], ["customers", "Customers"], ["cash", "Cash"], ["reports", "Reports"], ["users", "Users"], ["audit", "Audit"]];
  return (
    <div className="max-w-md mx-auto min-h-screen px-4 pt-6 pb-28">
      <div className="flex items-center justify-between mb-4">
        <div><h1 className="font-head text-lg font-extrabold leading-none">Admin Command Center</h1><div className="text-xs text-cyan-400/90">{user.name}</div></div>
        <button data-testid="admin-logout" onClick={logout} className="text-slate-400 flex items-center gap-1 text-sm"><LogOut size={16} /> Exit</button>
      </div>
      <div className="flex gap-1 overflow-x-auto bg-[#1C2541] p-1 rounded-xl mb-4 no-scrollbar">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={cx("px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap", tab === k ? "bg-cyan-500 text-slate-950" : "text-slate-400")}>{l}</button>
        ))}
      </div>
      {tab === "dash" && <DashTab />}
      {tab === "pay" && <PayDashTab />}
      {tab === "prices" && <PricesTab />}
      {tab === "customers" && <CustomersTab />}
      {tab === "cash" && <CashTab />}
      {tab === "reports" && <ReportsTab />}
      {tab === "users" && <UsersTab />}
      {tab === "audit" && <AuditTab />}
    </div>
  );
}

function DashTab() {
  const [d, setD] = useState(null);
  const [bays, setBays] = useState([]);
  const load = async () => {
    try { const [a, b] = await Promise.all([api.get("/admin/dashboard"), api.get("/bays")]); setD(a.data); setBays(b.data); } catch {}
  };
  useEffect(() => { load(); }, []);
  useInterval(load, 5000);
  if (!d) return <Card><div className="text-slate-500 text-center py-6">Loading…</div></Card>;
  return (
    <div className="space-y-4 animate-in">
      <div className="grid grid-cols-2 gap-3">
        <Kpi testid="kpi-today-sales" label="Today's Sales" value={peso(d.today_sales)} accent="text-emerald-400" />
        <Kpi testid="kpi-cars" label="Cars Serviced" value={d.cars_serviced} />
        <Kpi testid="kpi-avg-ticket" label="Avg Ticket" value={peso(d.avg_ticket)} />
        <Kpi testid="kpi-waiting" label="Waiting" value={d.vehicles_waiting} accent="text-amber-400" />
        <Kpi testid="kpi-servicing" label="In Service" value={d.vehicles_servicing} />
        <Kpi testid="kpi-bays" label="Bays Free" value={`${d.available_bays}/${d.total_bays}`} />
        <Kpi testid="kpi-avg-service" label="Avg Service" value={`${d.avg_service_time}m`} />
        <Kpi testid="kpi-avg-wait" label="Avg Wait" value={`${d.avg_wait_time}m`} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Kpi testid="kpi-new-cust" label="New Customers" value={d.new_customers} />
        <Kpi testid="kpi-repeat-cust" label="Repeat" value={d.repeat_customers} />
      </div>
      {d.last_cash_variance != null && (
        <Card className={cx(d.last_cash_variance < 0 ? "border-rose-500/40" : "border-emerald-500/40")}>
          <div className="text-xs text-slate-400">Last Cash Variance</div>
          <div className={cx("mono text-xl font-bold", d.last_cash_variance < 0 ? "text-rose-400" : "text-emerald-400")} data-testid="kpi-cash-variance">{peso(d.last_cash_variance)}</div>
        </Card>
      )}
      <div>
        <div className="ccc-label mb-2">Live Bay Operations</div>
        <div className="space-y-2">
          {bays.map((b) => (
            <Card key={b.id} data-testid={`admin-bay-${b.number}`} className="flex items-center justify-between">
              <div>
                <div className="font-head font-bold">Bay {b.number}</div>
                {b.order ? <div className="text-xs text-slate-400">{b.order.vehicle.plate} · {b.order.service.name}</div> : <div className="text-xs text-emerald-400">AVAILABLE</div>}
              </div>
              {b.order?.timers?.service_start ? <LiveTimer from={b.order.timers.service_start} to={b.order.timers.service_finish} targetMin={b.order.service.duration_min} /> :
                <span className={cx("text-xs font-bold px-2 py-1 rounded-full", b.status === "available" ? "text-emerald-400 bg-emerald-500/10" : "text-amber-400 bg-amber-500/10")}>{b.status.toUpperCase()}</span>}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

function PayDashTab() {
  const [range, setRange] = useState("today");
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/payments-dashboard", { params: { range } }).then((r) => setD(r.data)); }, [range]);
  return (
    <div className="space-y-4 animate-in">
      <RangePicker range={range} setRange={setRange} />
      {d && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Kpi testid="pay-expected" label="Expected" value={pesoShort(d.expected_collections)} />
            <Kpi testid="pay-actual" label="Actual" value={pesoShort(d.actual_collections)} accent="text-emerald-400" />
            <Kpi testid="pay-variance" label="Variance" value={pesoShort(d.variance)} accent={d.variance < 0 ? "text-rose-400" : "text-cyan-400"} />
          </div>
          <Card>
            <div className="ccc-label mb-2">Breakdown by Method</div>
            {Object.entries(d.breakdown).filter(([, v]) => v > 0).map(([k, v]) => (
              <div key={k} data-testid={`pay-break-${k}`} className="flex justify-between py-1.5 border-b border-slate-700/40 last:border-0">
                <span className="text-sm">{k}</span><span className="mono font-semibold">{peso(v)}</span>
              </div>
            ))}
            {Object.values(d.breakdown).every((v) => v === 0) && <div className="text-sm text-slate-500 py-2">No collections in range</div>}
          </Card>
        </>
      )}
    </div>
  );
}

function PricesTab() {
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [edit, setEdit] = useState(null);
  const [cfg, setCfg] = useState(null);
  const load = () => {
    api.get("/services", { params: { all: true } }).then((r) => setServices(r.data));
    api.get("/addons", { params: { all: true } }).then((r) => setAddons(r.data));
    api.get("/loyalty-config").then((r) => setCfg(r.data));
  };
  useEffect(() => { load(); }, []);

  const saveService = async () => {
    try { await api.put(`/services/${edit.id}`, edit); toast.success("Price updated — new orders use it, history unchanged"); setEdit(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (s) => { await api.patch(`/services/${s.id}/status`, { active: !s.active }); load(); };
  const saveAddon = async (a, price) => { try { await api.put(`/addons/${a.id}`, { ...a, price: Number(price) }); toast.success("Add-on saved"); load(); } catch (e) { toast.error(apiError(e)); } };
  const saveCfg = async () => { try { await api.put("/loyalty-config", cfg); toast.success("Loyalty config saved"); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-4 animate-in">
      <div className="ccc-label">Services</div>
      {services.map((s) => (
        <Card key={s.id} data-testid={`price-service-${s.id}`}>
          <div className="flex justify-between items-center">
            <div><div className="font-bold">{s.name}</div><div className="text-xs text-slate-400">{s.category} · {s.active ? "Active" : "Inactive"}</div></div>
            <div className="flex gap-2">
              <button data-testid={`price-toggle-${s.id}`} onClick={() => toggle(s)} className={cx("text-xs px-2 py-1 rounded-lg font-bold", s.active ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-600/20 text-slate-400")}>{s.active ? "ON" : "OFF"}</button>
              <button data-testid={`price-edit-${s.id}`} onClick={() => setEdit({ ...s, prices: { ...s.prices } })} className="text-xs px-3 py-1 rounded-lg bg-cyan-500/15 text-cyan-400 font-bold">Edit</button>
            </div>
          </div>
          {edit?.id === s.id && (
            <div className="mt-3 space-y-2 border-t border-slate-700/50 pt-3">
              {Object.keys(edit.prices).map((vt) => (
                <div key={vt} className="flex items-center gap-2">
                  <span className="text-xs w-24 text-slate-400">{vt}</span>
                  <input className="ccc-input" type="number" data-testid={`price-input-${s.id}-${vt}`} value={edit.prices[vt]} onChange={(e) => setEdit({ ...edit, prices: { ...edit.prices, [vt]: Number(e.target.value) } })} />
                </div>
              ))}
              <div className="flex gap-2 pt-1">
                <Btn className="flex-1" data-testid={`price-save-${s.id}`} onClick={saveService}>Save</Btn>
                <Btn variant="ghost" onClick={() => setEdit(null)}>Cancel</Btn>
              </div>
            </div>
          )}
        </Card>
      ))}

      <div className="ccc-label pt-2">Add-ons</div>
      {addons.map((a) => (
        <Card key={a.id} className="flex items-center gap-2" data-testid={`price-addon-${a.id}`}>
          <span className="text-sm flex-1">{a.name}</span>
          <input className="ccc-input w-28" type="number" defaultValue={a.price} data-testid={`addon-input-${a.id}`} onBlur={(e) => Number(e.target.value) !== a.price && saveAddon(a, e.target.value)} />
        </Card>
      ))}

      {cfg && (
        <>
          <div className="ccc-label pt-2">Loyalty Config</div>
          <Card className="space-y-2">
            <NumRow label="Points per ₱100 spent" v={cfg.points_per_100} onChange={(v) => setCfg({ ...cfg, points_per_100: v })} tid="cfg-ppp" />
            <NumRow label="₱ per point (redeem)" v={cfg.peso_per_point} onChange={(v) => setCfg({ ...cfg, peso_per_point: v })} tid="cfg-ppr" />
            <NumRow label="Free wash after N visits" v={cfg.free_service_threshold} onChange={(v) => setCfg({ ...cfg, free_service_threshold: v })} tid="cfg-free" />
            <NumRow label="VIP visits threshold" v={cfg.vip_visits} onChange={(v) => setCfg({ ...cfg, vip_visits: v })} tid="cfg-vip" />
            <Btn className="w-full mt-1" data-testid="cfg-save" onClick={saveCfg}>Save Loyalty Config</Btn>
          </Card>
        </>
      )}
    </div>
  );
}

function NumRow({ label, v, onChange, tid }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm flex-1 text-slate-300">{label}</span>
      <input className="ccc-input w-24" type="number" value={v} data-testid={tid} onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}

function CustomersTab() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const load = () => api.get("/customers", { params: { search } }).then((r) => setList(r.data));
  useEffect(() => { load(); }, [search]);
  const open = (c) => api.get(`/customers/${c.id}`).then((r) => setSel(r.data));

  if (sel) return (
    <div className="space-y-3 animate-in">
      <button className="text-slate-400 text-sm" onClick={() => setSel(null)}>← Back to list</button>
      <Card className="border-cyan-500/30">
        <div className="font-head text-lg font-bold">{sel.name} <span className="text-xs text-cyan-400">({sel.status})</span></div>
        <div className="text-xs text-slate-400">{sel.mobile}</div>
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div><div className="text-xs text-slate-400">Visits</div><div className="mono font-bold" data-testid="cust-detail-visits">{sel.visits}</div></div>
          <div><div className="text-xs text-slate-400">Lifetime</div><div className="mono font-bold" data-testid="cust-detail-spend">{peso(sel.lifetime_spend)}</div></div>
          <div><div className="text-xs text-slate-400">Points</div><div className="mono font-bold">{sel.points}</div></div>
          <div><div className="text-xs text-slate-400">Avg Ticket</div><div className="mono font-bold">{peso(sel.avg_ticket)}</div></div>
          <div className="col-span-2"><div className="text-xs text-slate-400">Favorite</div><div className="text-sm font-medium">{sel.favorite_service || "—"}</div></div>
        </div>
      </Card>
      <div className="ccc-label">Transaction History ({sel.orders?.length || 0})</div>
      {sel.orders?.map((o) => (
        <Card key={o.id} className="flex justify-between text-sm">
          <div><div className="mono text-cyan-400">{o.transaction_no}</div><div className="text-xs text-slate-400">{o.service.name} · {new Date(o.created_at).toLocaleDateString()}</div></div>
          <div className="mono font-bold">{peso(o.total)}</div>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="space-y-3 animate-in">
      <Field testid="cust-search" placeholder="Search name or mobile" value={search} onChange={(e) => setSearch(e.target.value)} />
      {list.map((c) => (
        <Card key={c.id} data-testid={`admin-cust-${c.id}`} onClick={() => open(c)} className="flex justify-between items-center">
          <div><div className="font-bold">{c.name}</div><div className="text-xs text-slate-400">{c.mobile} · {c.visits} visits</div></div>
          <div className="text-right"><div className="text-xs text-cyan-400 font-bold">{c.status}</div><div className="mono text-sm">{peso(c.lifetime_spend)}</div></div>
        </Card>
      ))}
    </div>
  );
}

function CashTab() {
  const [session, setSession] = useState(null);
  const [counts, setCounts] = useState({});
  const [result, setResult] = useState(null);
  const [sessions, setSessions] = useState([]);
  const load = () => { api.get("/cash/current").then((r) => setSession(r.data)); api.get("/cash/sessions").then((r) => setSessions(r.data)); };
  useEffect(() => { load(); }, []);

  const counted = DENOMS.reduce((s, d) => s + d * (Number(counts[d]) || 0), 0);
  const close = async () => {
    try {
      const denominations = {}; DENOMS.forEach((d) => { denominations[d] = Number(counts[d]) || 0; });
      const { data } = await api.post("/cash/close", { denominations });
      setResult(data); toast.success("Session closed & reconciled"); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const openQuick = async () => { try { await api.post("/cash/open", { opening_amount: 5000 }); toast.success("Opened with ₱5,000"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-4 animate-in">
      {!session?.open ? (
        <Card><p className="text-sm text-slate-400 mb-3">No open cash session. Open one to start reconciliation.</p><Btn className="w-full" data-testid="admin-cash-open" onClick={openQuick}>Open Session (₱5,000)</Btn></Card>
      ) : (
        <>
          <Card>
            <div className="ccc-label mb-1">Session Open</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-slate-400">Opening</span><div className="mono font-bold">{peso(session.opening_amount)}</div></div>
              <div><span className="text-slate-400">Cash Sales</span><div className="mono font-bold">{peso(session.cash_payments)}</div></div>
              <div className="col-span-2"><span className="text-slate-400">Expected Cash</span><div className="mono text-xl font-extrabold text-cyan-400" data-testid="admin-expected-cash">{peso(session.expected_cash)}</div></div>
            </div>
          </Card>
          <Card>
            <div className="ccc-label mb-2">Closing Count — Peso Denominations</div>
            {DENOMS.map((d) => (
              <div key={d} className="flex justify-between items-center bg-[#0B132B] p-2.5 rounded-lg border border-slate-700/60 mb-2">
                <span className="mono text-sm w-16">₱{d.toLocaleString()}</span>
                <input className="ccc-input w-20" type="number" min="0" data-testid={`denom-${d}`} value={counts[d] || ""} onChange={(e) => setCounts({ ...counts, [d]: e.target.value })} placeholder="0" />
                <span className="mono text-sm text-cyan-400 w-24 text-right">{peso(d * (Number(counts[d]) || 0))}</span>
              </div>
            ))}
            <div className="flex justify-between mt-2"><span className="font-bold">Counted</span><span className="mono text-xl font-extrabold text-cyan-400" data-testid="admin-counted-cash">{peso(counted)}</span></div>
            <Btn className="w-full mt-3" data-testid="admin-cash-close" onClick={close}>Close & Reconcile</Btn>
          </Card>
        </>
      )}
      {result && (
        <Card className={cx(result.variance < 0 ? "border-rose-500/40" : "border-emerald-500/40")}>
          <div className="ccc-label mb-2">Reconciliation Result</div>
          <div className="flex justify-between text-sm"><span>Expected</span><span className="mono">{peso(result.expected_cash)}</span></div>
          <div className="flex justify-between text-sm"><span>Counted</span><span className="mono">{peso(result.counted_cash)}</span></div>
          <div className="flex justify-between font-bold mt-1"><span>Variance</span><span className={cx("mono", result.variance < 0 ? "text-rose-400" : "text-emerald-400")} data-testid="cash-result-variance">{peso(result.variance)}</span></div>
        </Card>
      )}
      <div className="ccc-label pt-2">Recent Sessions</div>
      {sessions.slice(0, 5).map((s) => (
        <Card key={s.id} className="flex justify-between text-sm">
          <div><div>{new Date(s.opened_at).toLocaleDateString()}</div><div className="text-xs text-slate-400">{s.status}</div></div>
          {s.status === "closed" && <div className={cx("mono", s.variance < 0 ? "text-rose-400" : "text-emerald-400")}>{peso(s.variance)}</div>}
        </Card>
      ))}
    </div>
  );
}

function ReportsTab() {
  const [range, setRange] = useState("today");
  const [rep, setRep] = useState(null);
  const [eod, setEod] = useState(null);
  useEffect(() => { api.get("/admin/reports", { params: { range } }).then((r) => setRep(r.data)); }, [range]);
  const genEod = async () => { try { const { data } = await api.post("/admin/eod"); setEod(data); toast.success("EOD generated"); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-4 animate-in">
      <RangePicker range={range} setRange={setRange} />
      {rep && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Kpi testid="rep-net" label="Net Sales" value={peso(rep.net_sales)} accent="text-emerald-400" />
            <Kpi testid="rep-gross" label="Gross" value={peso(rep.gross_sales)} />
            <Kpi testid="rep-txns" label="Transactions" value={rep.transactions} />
            <Kpi testid="rep-avg" label="Avg Ticket" value={peso(rep.avg_ticket)} />
            <Kpi testid="rep-discounts" label="Discounts" value={peso(rep.discounts)} accent="text-amber-400" />
            <Kpi testid="rep-cars" label="Cars Serviced" value={rep.cars_serviced} />
          </div>
          <Card>
            <div className="ccc-label mb-2">Top Performers</div>
            <div className="flex justify-between text-sm py-1"><span className="text-slate-400">Top Service</span><span>{rep.top_service || "—"}</span></div>
            <div className="flex justify-between text-sm py-1"><span className="text-slate-400">Top Add-on</span><span>{rep.top_addon || "—"}</span></div>
            <div className="flex justify-between text-sm py-1"><span className="text-slate-400">Peak Hour</span><span>{rep.peak_hour || "—"}</span></div>
            <div className="flex justify-between text-sm py-1"><span className="text-slate-400">Avg Service Time</span><span>{rep.avg_service_time}m</span></div>
          </Card>
          <Card>
            <div className="ccc-label mb-2">Revenue per Bay</div>
            {rep.bay_kpis.map((b) => (
              <div key={b.bay} className="flex justify-between items-center py-1.5 border-b border-slate-700/40 last:border-0" data-testid={`bay-kpi-${b.bay}`}>
                <span className="text-sm">Bay {b.bay} · {b.cars} cars</span>
                <div className="text-right"><div className="mono text-sm text-cyan-400">{peso(b.revenue)}</div><div className="text-xs text-slate-500">{peso(b.revenue_per_hour)}/hr</div></div>
              </div>
            ))}
          </Card>
          <Card>
            <div className="ccc-label mb-2">Customer Segments</div>
            <div className="grid grid-cols-4 gap-2 text-center">
              {Object.entries(rep.customer_segments).map(([k, v]) => (
                <div key={k}><div className="mono text-xl font-bold text-cyan-400">{v}</div><div className="text-xs text-slate-400">{k}</div></div>
              ))}
            </div>
          </Card>
        </>
      )}
      <Btn className="w-full" data-testid="admin-gen-eod" onClick={genEod}>Generate End-of-Day Report</Btn>
      {eod && (
        <Card className="print-area">
          <div className="text-center mb-2"><div className="font-head font-extrabold">END OF DAY — {eod.date}</div><div className="text-xs text-slate-400">{eod.branch}</div></div>
          <div className="flex justify-between text-sm py-0.5"><span>Cars Serviced</span><span className="mono">{eod.cars_serviced}</span></div>
          <div className="flex justify-between text-sm py-0.5"><span>Gross Sales</span><span className="mono">{peso(eod.gross_sales)}</span></div>
          <div className="flex justify-between text-sm py-0.5"><span>Discounts</span><span className="mono">{peso(eod.discounts)}</span></div>
          <div className="flex justify-between text-sm font-bold py-0.5"><span>Net Sales</span><span className="mono">{peso(eod.net_sales)}</span></div>
          <div className="flex justify-between text-sm py-0.5"><span>Transactions</span><span className="mono">{eod.transactions}</span></div>
          <div className="border-t border-slate-700/50 my-2" />
          <div className="ccc-label mb-1">Payments</div>
          {Object.entries(eod.payment_breakdown).filter(([, v]) => v > 0).map(([k, v]) => <div key={k} className="flex justify-between text-sm py-0.5"><span>{k}</span><span className="mono">{peso(v)}</span></div>)}
          <div className="border-t border-slate-700/50 my-2" />
          <div className="flex justify-between text-sm py-0.5"><span>Cash Expected</span><span className="mono">{eod.cash.expected != null ? peso(eod.cash.expected) : "—"}</span></div>
          <div className="flex justify-between text-sm py-0.5"><span>Cash Actual</span><span className="mono">{eod.cash.actual != null ? peso(eod.cash.actual) : "—"}</span></div>
          <div className="flex justify-between text-sm py-0.5"><span>Over/Short</span><span className="mono">{eod.cash.variance != null ? peso(eod.cash.variance) : "—"}</span></div>
          <Btn variant="ghost" className="w-full mt-3 no-print flex items-center justify-center gap-2" onClick={() => window.print()}><Printer size={16} /> Print EOD</Btn>
        </Card>
      )}
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [hist, setHist] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setLogs(r.data)); api.get("/price-history").then((r) => setHist(r.data)); }, []);
  return (
    <div className="space-y-4 animate-in">
      <div className="ccc-label">Price Change History</div>
      {hist.length === 0 && <Card><div className="text-sm text-slate-500 text-center py-2">No price changes yet</div></Card>}
      {hist.map((h) => (
        <Card key={h.id} className="text-sm" data-testid={`price-hist-${h.id}`}>
          <div className="flex justify-between"><span className="font-medium">{h.entity_name}{h.vehicle_type ? ` · ${h.vehicle_type}` : ""}</span><span className="text-xs text-slate-400">{new Date(h.timestamp).toLocaleString()}</span></div>
          <div className="mono text-xs mt-1">{h.old_price != null ? peso(h.old_price) : "—"} → <span className="text-cyan-400">{peso(h.new_price)}</span> · by {h.changed_by}</div>
        </Card>
      ))}
      <div className="ccc-label pt-2">Audit Log</div>
      {logs.slice(0, 40).map((l) => (
        <Card key={l.id} className="text-sm" data-testid={`audit-${l.id}`}>
          <div className="flex justify-between"><span className="font-medium">{l.action}</span><span className="text-xs text-slate-400">{new Date(l.timestamp).toLocaleString()}</span></div>
          <div className="text-xs text-slate-400 mt-0.5">{l.entity} · {l.user_name || "system"}{l.reason ? ` · ${l.reason}` : ""}</div>
        </Card>
      ))}
    </div>
  );
}

function UsersTab() {
  const { user: me } = useAuth();
  const isSuper = me?.role === "superadmin";
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState("cashier");
  const [f, setF] = useState({ name: "", email: "", password: "", username: "", pin: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const load = () => api.get("/auth/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    const payload = { name: f.name, role };
    if (["admin", "superadmin"].includes(role)) { payload.email = f.email; payload.password = f.password; }
    else { payload.username = f.username; payload.pin = f.pin; }
    try {
      await api.post("/auth/users", payload);
      toast.success("User created");
      setF({ name: "", email: "", password: "", username: "", pin: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (u) => {
    try { await api.patch(`/auth/users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const [resetId, setResetId] = useState(null);
  const [resetVal, setResetVal] = useState("");
  const doReset = async (u) => {
    const adminRole = ["admin", "superadmin"].includes(u.role);
    try {
      await api.patch(`/auth/users/${u.id}`, adminRole ? { password: resetVal } : { pin: resetVal });
      toast.success(adminRole ? "Password reset" : "PIN reset");
      setResetId(null); setResetVal(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try { await api.delete(`/auth/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4 animate-in">
      <Card>
        <div className="ccc-label mb-2">Add User</div>
        <label className="block mb-2">
          <span className="ccc-label">Role</span>
          <select className="ccc-input" data-testid="user-role" value={role} onChange={(e) => setRole(e.target.value)}>
            {isSuper && <option value="superadmin">Super Admin</option>}
            {isSuper && <option value="admin">Admin (owner/manager)</option>}
            <option value="supervisor">Supervisor</option>
            <option value="cashier">Cashier</option>
            <option value="bay_crew">Bay Crew</option>
            <option value="customer_service">Customer Service</option>
          </select>
        </label>
        <Field label="Full Name" testid="user-name" value={f.name} onChange={set("name")} />
        {["admin", "superadmin"].includes(role) ? (
          <div className="space-y-2 mt-2">
            <Field label="Email" testid="user-email" value={f.email} onChange={set("email")} />
            <Field label="Password (min 6 chars)" type="password" testid="user-password" value={f.password} onChange={set("password")} />
            <p className="text-xs text-slate-500">Admins sign in with email + password at the Admin screen.</p>
          </div>
        ) : (
          <div className="space-y-2 mt-2">
            <Field label="Username" testid="user-username" value={f.username} onChange={set("username")} />
            <Field label="PIN (min 4 digits)" type="password" testid="user-pin" value={f.pin} onChange={set("pin")} />
            <p className="text-xs text-slate-500">Crew sign in with username + PIN at the Crew screen.</p>
          </div>
        )}
        <Btn className="w-full mt-3" data-testid="user-create" onClick={create}>Create User</Btn>
      </Card>

      <div className="ccc-label">All Users ({users.length})</div>
      {users.map((u) => {
        const adminRole = ["admin", "superadmin"].includes(u.role);
        const canDelete = u.id !== me?.id && u.role !== "superadmin" && (u.role !== "admin" || isSuper);
        return (
          <Card key={u.id} data-testid={`user-row-${u.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold">{u.name} {!u.active && <span className="text-xs text-rose-400">· disabled</span>}</div>
                <div className="text-xs text-slate-400">{u.role} · {u.email || u.username}</div>
              </div>
              <button data-testid={`user-toggle-${u.id}`} onClick={() => toggle(u)}
                className={cx("text-xs px-3 py-1.5 rounded-lg font-bold", u.active ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-600/20 text-slate-400")}>
                {u.active ? "Active" : "Disabled"}
              </button>
            </div>
            <div className="flex gap-2 mt-3">
              <button data-testid={`user-reset-${u.id}`} onClick={() => { setResetId(resetId === u.id ? null : u.id); setResetVal(""); }}
                className="text-xs px-3 py-1.5 rounded-lg font-bold bg-cyan-500/15 text-cyan-400">
                {adminRole ? "Reset Password" : "Reset PIN"}
              </button>
              {canDelete && (
                <button data-testid={`user-delete-${u.id}`} onClick={() => remove(u)}
                  className="text-xs px-3 py-1.5 rounded-lg font-bold bg-rose-500/15 text-rose-400">Delete</button>
              )}
            </div>
            {resetId === u.id && (
              <div className="flex gap-2 mt-2">
                <input className="ccc-input" data-testid={`user-reset-input-${u.id}`}
                  placeholder={adminRole ? "New password (min 6)" : "New PIN (min 4 digits)"}
                  value={resetVal} onChange={(e) => setResetVal(e.target.value)} />
                <Btn data-testid={`user-reset-save-${u.id}`} onClick={() => doReset(u)}>Save</Btn>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function RangePicker({ range, setRange }) {
  const opts = [["today", "Today"], ["yesterday", "Yesterday"], ["week", "Week"], ["month", "Month"]];
  return (
    <div className="flex gap-1.5">
      {opts.map(([k, l]) => (
        <button key={k} data-testid={`range-${k}`} onClick={() => setRange(k)} className={cx("flex-1 py-2 rounded-lg text-xs font-bold border", range === k ? "bg-cyan-500 text-slate-950 border-cyan-400" : "bg-[#1C2541] border-slate-700 text-slate-400")}>{l}</button>
      ))}
    </div>
  );
}
